import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import "./app-card.css";
import { Routes, Route, useMatch, Link } from "react-router-dom";
import { TermsConditions } from "./TermsConditions";
import { Privacy } from "./Privacy";
import { CookiePolicy } from "./CookiePolicy";
import { CookieConsent } from "./CookieConsent";

function App() {
  const queryParams = new URLSearchParams(window.location.search);
  const isFullscreen = queryParams.has('fullscreen');
  const routes = (
    <Routes>
      <Route exact path="/a/:game" element={<GameApp />} />
      <Route exact path="/terms-conditions" element={<TermsConditions />} />
      <Route exact path="/privacy-policy" element={<Privacy />} />
      <Route exact path="/cookies" element={<CookiePolicy />} />
      <Route index element={<GameSection />} />
    </Routes>
  );
  return (
    isFullscreen ?
      routes
      :
      <div className="app-body">
        <div>
          <Header />
          <section
            id="site-body"
            className="uk-section uk-section-small uk-section-default uk-padding-remove-bottom uk-padding-remove-top"
          >
            {routes}
          </section>
          <Footer />
        </div>
      </div>
  );
}

export default App;

function Header() {
  return (
    <header id="site-head">
      <CookieConsent />
      <nav className="uk-navbar-container primary-colour" uk-navbar="true">
        <div className="uk-navbar-left" style={{ position: "relative" }}>
          <a href="/" id="site-header" className="underline-hover">
            Synaren Play Games
          </a>
        </div>
        <div className="uk-navbar-right">
          <ul className="uk-navbar-nav">
            <li className="uk-active">
              <a href="/">Games</a>
            </li>
          </ul>
        </div>
      </nav>
    </header>
  );
}

function Footer() {
  return (
    <footer
      id="site-foot"
      className="uk-section uk-section-secondary uk-section-xsmall"
    >
      <div className="uk-container uk-text-small uk-text-center">
        <div uk-grid="true">
          <li className="uk-width-1-2 uk-text-left uk-padding-left-medium uk-padding-right-medium">
            <small>
              © 2020 - {new Date().getFullYear()} Synaren ({process.env.REACT_APP_API_VERSION_NUMBER})
            </small>
          </li>
          <ul
            id="footer-sup"
            className="uk-width-1-2 uk-subnav uk-subnav-divider"
            style={{ margin: "auto" }}
          >
            <a href="contact-us" className="uk-icon-link">
              Contact Us
            </a>
            <a href="/terms-conditions" className="uk-icon-link">
              Terms and Conditions
            </a>
            <a href="/privacy-policy" className="uk-icon-link">
              Privacy Policy
            </a>
            <a href="/cookies" className="uk-icon-link">
              Cookies
            </a>
          </ul>
        </div>
      </div>
    </footer>
  );
}

function GameApp() {
  let match = useMatch("/a/:game");
  const queryParams = new URLSearchParams(window.location.search);
  const isFullscreen = queryParams.has('fullscreen');

  useEffect(() => {
    const script = document.createElement("script");
    script.src = `/games/${match.params.game}/index.js`;
    document.body.appendChild(script);
  }, []);

  return (
    isFullscreen ?
      <div id="renderer"></div> :
      (<div id="game-container">
        <div id="renderer">
          <GameController />
        </div>
      </div>)
  );
}

function GameController() {
  const [edit, setEdit] = useState(false);
  const [_, setUsername] = useState();
  const { SynarenOnline } = window;
  const isOnlineEnabled = SynarenOnline && SynarenOnline.Socket;
  const toggleFullscreen = () => {
    let elem = document.getElementById("game-container");

    if (!document.fullscreenElement) {
      elem.requestFullscreen().catch((err) => {
        console.err(
          `Error attempting to enable full-screen mode: ${err.message} (${err.name})`
        );
      });
    } else {
      document.exitFullscreen();
    }
  };

  const toggleEdit = useCallback(() => {
    setEdit(!edit);
  }, [edit]);

  const username =
    SynarenOnline && SynarenOnline.Socket
      ? `Username: ${SynarenOnline.Socket.getLocalName() == null
        ? SynarenOnline.Socket.createLocalName()
        : SynarenOnline.Socket.getLocalName()
      }`
      : undefined;

  return (
    <div
      id="game-controller"
      className="primary-colour-inverted site-border site-card"
    >
      <span style={{ position: "relative" }}>
        {isOnlineEnabled && username != null ? (
          <span
            tabIndex="0"
            onClick={toggleEdit}
            className="uk-icon-link uk-button-primary uk-margin-small-right"
            uk-icon="icon: pencil"
          ></span>
        ) : (
          ""
        )}
        <span style={{ verticalAlign: "bottom" }}>{username}</span>
        {isOnlineEnabled && edit ? (
          <input
            style={{ width: "150px", height: "30px", marginLeft: '35px' }}
            onChange={(e) => {
              SynarenOnline.Socket.setLocalName(e.target.value);
              setUsername(e.target.value);
            }}
            className="uk-input float-bottom site-border-small"
            type="text"
            placeholder="Input"
          />
        ) : undefined}
      </span>

      <span style={{ display: "none" }}>
        {isOnlineEnabled ? SynarenOnline.Socket.getLocalClientId() : ""}
      </span>
      <span
        id="fullscreen-game-button"
        tabIndex="0"
        onClick={toggleFullscreen}
        className="uk-icon-link uk-button-primary"
        uk-icon="icon: expand"
      ></span>
    </div>
  );
}

function GameSection() {
  return (
    <div className="uk-container uk-container-expand uk-margin-large-bottom">
      <div
        className="uk-grid uk-grid-medium uk-child-width-1-2@s uk-child-width-1-3@m uk-child-width-1-4@l  uk-child-width-1-5@xl uk-grid-match js-filter"
        data-uk-grid="masonry: true"
        data-uk-sortable="handle: .drag-icon"
      >
        <AppCard
          title="Chess"
          name="chess-app"
          tags="Play chess against a friend or computer"
        />
        <AppCard
          title="Space Duck Platformer"
          name="space-duck-platformer"
          tags="Space Duck Platformer is a platforming game utilising your physics skills, make sure to time your jumps correctly to reach the next platform."
        />
        <AppCard
          title="Space Duck Asteroids"
          name="space-duck-asteroids"
          tags="Space Duck Asteroids is an arcade obstacle avoidance game where you avoid asteroids. The best way to survive is to follow Guardian Duck who can destroy the asteroids with lasers."
        />
        <AppCard
          title="Cats Tower Defense"
          name="space-duck-tower-defense"
          tags="Cats Tower Defense is a tower defense game where you place towers to defend against waves of enemies."
        />
          <AppCard
            title="Chimney Dash"
            name="chimney-dash"
            tags="In Chimney Dash, you play as one of three cats or two humans flying a sleigh above a snowy town, dropping presents into chimneys to spread holiday joy. Each successful drop into a chimney extends your flight time, while missed drops slowly bring you closer to landing."
          />
        <Experimental>
          <AppCard title="Gabbo's Guild" name="gabbos-guild" tags="" />
        </Experimental>
      </div>
    </div>
  );
}

function Experimental({ children }) {
  return window.location.href.includes("localhost") ? children : <p />
}


function AppCard({ title, tags, name }) {
  return (
    <div className="app-card" data-tags={tags}>
      <div className="uk-card uk-card-small uk-card-default">
        <div className="uk-card-header">
          <div className="uk-grid uk-grid-small uk-text-small" data-uk-grid>
            <div className="uk-width-expand">
              <a href={`/a/${name}`}>
                <span className="cat-txt">{title}</span>
              </a>
            </div>
          </div>
        </div>
        <div className="uk-card-media">
          <div className="uk-inline-clip uk-transition-toggle">
            <a href={`/a/${name}`}>
              <img
                className="lazy"
                alt=""
                src={`${window.location.origin}/games/${name}/${name}-screen.png`}
              />
            </a>
            <div className="uk-transition-slide-bottom uk-position-bottom uk-overlay uk-overlay-primary">
              <a href={`/a/${name}`} tabIndex="-1">
                <span data-uk-icon="icon:play-circle; ratio: 0.8" /> Play
              </a>
            </div>
          </div>
        </div>
        <div className="uk-card-body">
          <h6 className="uk-margin-small-bottom uk-margin-remove-adjacent uk-text-bold">
            {title}
          </h6>
          <p className="uk-text-small uk-text-muted">{tags}</p>
        </div>
      </div>
    </div>
  );
}
